// ─── COMPTES : liste, recherche, forfait et statut ────────────────
// Écran principal de l'app d'administration. Recherche par e-mail/nom, puis
// tap = feuille d'édition (plan, statut, rôles). Lecture réservée aux admins
// par les règles Firestore ; le commerce voit et change le FORFAIT.
//
// Une identité IVV est PARTAGÉE par plusieurs comptes (cf. `groupeIVV`) : la
// liste les réunit sous un même en-tête, sinon on ne voit pas qu'il s'agit du
// même valideur derrière trois adresses.
import React, { useEffect, useMemo, useState } from 'react';
import { View, Text, ScrollView, TouchableOpacity, StyleSheet } from 'react-native';
import {
  PageHeader, EmptyState, BarreRecherche, FeuilleFiltres, LigneSwipe, stylesForm, useUI, useT, useAuth,
  aDemandeForfait, ecouterPlans, nomPlan, confirmerSuppression, confirmerChoix,
} from '@mop/core';
import { ecouterComptes, appliquerDemandeForfait, refuserDemandeForfait, PLANS, STATUTS } from './services/admin';
import { supprimerCompte, supprimerCompteDefinitif } from './services/comptes';
import FeuilleNouveauCompte from './FeuilleNouveauCompte';
import FeuilleCompte from './FeuilleCompte';
import CarteCompte from './CarteCompte';

const SANS_IVV = '__sans';   // valeur interne : compte sans identité de valideur

// Groupes de filtres (mêmes données pour la barre et la fenêtre). Chaque
// groupe est une MULTI-sélection : on coche ce qu'on veut voir ; tout coché
// (ou rien enregistré) = aucun filtre.
const GROUPES = (t, plansCfg) => ([
  { cle: 'plan', titre: t('admin.forfait'), emoji: '🏷️',
    options: PLANS.map(p => ({ id: p, label: nomPlan(plansCfg, p) })) },
  { cle: 'statut', titre: t('admin.statut'), emoji: '📌',
    options: [
      ...STATUTS.map(x => ({ id: x, label: t(`admin.statut_${x}`) })),
      { id: 'supprime', label: t('admin.statut_supprime') },
    ] },
  // Deux options pour que « tout coché » veuille bien dire « aucun filtre ».
  { cle: 'attente', titre: t('admin.aTraiter'), emoji: '⏳',
    options: [
      { id: 'avec', label: t('admin.avecDemande') },
      { id: 'sans', label: t('admin.sansDemande') },
    ] },
  // Valideur ou non : la question utile est « c'est un compte IVV ? », pas
  // « lequel » — les identités elles-mêmes servent à REGROUPER la liste.
  { cle: 'ivv', titre: t('admin.ivv'), emoji: '🐞',
    options: [
      { id: 'oui', label: t('admin.avecIVV') },
      { id: 'non', label: t('admin.sansIVV') },
    ] },
]);

export default function EcranComptes() {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const { estAdmin } = useAuth();
  const [comptes, setComptes] = useState([]);
  const [err, setErr] = useState('');
  const [q, setQ] = useState('');
  // Filtres en UN objet (brouillon dans la feuille, appliqué à la 💾).
  const [filtres, setFiltres] = useState({});   // { cle: [ids cochés] }
  const [feuilleFiltres, setFeuilleFiltres] = useState(false);
  const [edit, setEdit] = useState(null);
  // Clic = fiche en LECTURE SEULE (bouton ✏️ pour passer en édition) ; glisser
  // à droite (LigneSwipe) = édition directe — demande de Cédric (23/08).
  const [editionDirecte, setEditionDirecte] = useState(false);
  const [nouveau, setNouveau] = useState(false);
  const [busyUid, setBusyUid] = useState(null);
  // Noms réels des forfaits (config/plans) — pour la puce de la carte ET les
  // libellés du filtre, plutôt que les identifiants bruts (`gratuit`…).
  const [plansCfg, setPlansCfg] = useState({});
  useEffect(() => ecouterPlans(setPlansCfg), []);

  useEffect(() => ecouterComptes(setComptes, (e) => setErr(e?.message || String(e))), []);

  // Accepter/refuser une demande de forfait SANS ouvrir la fiche du compte.
  const traiterDemande = async (c, fn) => {
    setBusyUid(c.uid); setErr('');
    try { await fn(); } catch (e) { setErr(e?.message || String(e)); }
    setBusyUid(null);
  };
  const accepterDepuisListe = (c) => traiterDemande(c, () => appliquerDemandeForfait(c.uid, c.planDemande));
  const refuserDepuisListe = (c) => traiterDemande(c, () => refuserDemandeForfait(c.uid));

  const ouvrirFiche  = (c) => { setEdit(c); setEditionDirecte(false); };
  const ouvrirEdition = (c) => { setEdit(c); setEditionDirecte(true); };
  // Un compte déjà en corbeille (statut=supprime) : un second swipe/suppression
  // n'a plus rien à « mettre en corbeille » — il passe donc à la suppression
  // DÉFINITIVE (identité Auth + données), irréversible, confirmée fort.
  const supprimerDepuisListe = (c) => (c.statut === 'supprime'
    ? confirmerSuppression(t('admin.supprimerDefinitif'), t('admin.supprimerDefinitifMsg', { email: c.email }),
        () => traiterDemande(c, () => supprimerCompteDefinitif(c.uid)))
    : confirmerSuppression(t('admin.supprimerCompte'), t('admin.supprimerCompteMsg', { email: c.email }),
        () => traiterDemande(c, () => supprimerCompte(c.uid))));
  // Filet web : le swipe de `LigneSwipe` est natif uniquement.
  const menuWeb = (c) => confirmerChoix(c.email, t('admin.queFaireCompte'), [
    { text: t('commun.editer'), onPress: () => ouvrirEdition(c) },
    { text: t(c.statut === 'supprime' ? 'admin.supprimerDefinitif' : 'commun.supprimer'), style: 'destructive', onPress: () => supprimerDepuisListe(c) },
    { text: t('commun.annuler'), style: 'cancel' },
  ]);

  const groupes = useMemo(() => GROUPES(t, plansCfg), [t, plansCfg]);
  // Un groupe filtre s'il est PRÉSENT dans `filtres` (absent = tout) — même
  // règle que l'ancien BarreFiltres, désormais calculée ici (le gabarit
  // générique ignore la FORME des filtres, laissée à chaque écran).
  const nbFiltres = groupes.filter(g => Array.isArray(filtres[g.cle])).length;

  // Ce qui ATTEND une décision remonte en tête (demande, inscription).
  const urgence = (c) => (aDemandeForfait(c) ? 2 : 0) + ((c.statut || '') === 'en_attente' ? 1 : 0);

  // Comptes retenus, PUIS regroupés par identité IVV. Les groupes qui portent
  // une urgence remontent ; les comptes sans identité ferment la liste.
  const sections = useMemo(() => {
    const rq = q.trim().toLowerCase();
    const retenus = comptes.filter(c => {
      // Règle unique : un groupe ABSENT de `filtres` ne filtre pas ; sinon la
      // valeur du compte doit être cochée (liste vide ⇒ rien ne passe).
      const garde = (cle, valeur) => {
        const coches = filtres[cle];
        return !Array.isArray(coches) || coches.includes(valeur);
      };
      if (!garde('plan', c.plan || 'gratuit')) return false;
      if (!garde('statut', c.statut || 'actif')) return false;
      if (!garde('attente', aDemandeForfait(c) ? 'avec' : 'sans')) return false;
      if (!garde('ivv', c.groupeIVV ? 'oui' : 'non')) return false;
      if (!rq) return true;
      return `${c.email || ''} ${c.prenom || ''} ${c.nom || ''} ${c.groupeIVV || ''}`.toLowerCase().includes(rq);
    });

    const parIvv = new Map();
    for (const c of retenus) {
      const cle = c.groupeIVV || SANS_IVV;
      if (!parIvv.has(cle)) parIvv.set(cle, []);
      parIvv.get(cle).push(c);
    }
    return [...parIvv.entries()]
      .map(([cle, liste]) => ({
        cle,
        nom: cle === SANS_IVV ? '' : cle,
        comptes: liste.sort((a, b) => urgence(b) - urgence(a)),
        urgence: Math.max(...liste.map(urgence)),
      }))
      .sort((a, b) => {
        if (a.cle === SANS_IVV) return 1;              // les orphelins en dernier
        if (b.cle === SANS_IVV) return -1;
        return (b.urgence - a.urgence) || a.nom.localeCompare(b.nom);
      });
  }, [comptes, q, filtres]);

  const total = sections.reduce((n, sec) => n + sec.comptes.length, 0);
  // Compte TOUJOURS sur `comptes` (pas `sections`) : le total doit rester
  // visible même quand un autre filtre masque les demandes en attente.
  const nDemandes = useMemo(() => comptes.filter(aDemandeForfait).length, [comptes]);

  return (
    <View style={s.page}>
      <PageHeader titre={t('admin.comptes')} iconeTitre="👥"
        action={estAdmin ? { icone: '＋', label: t('admin.nouveauCompte'), onPress: () => setNouveau(true) } : undefined} />
      {/* UNE seule barre : 🔽 (fenêtre de filtres) · recherche libre
          nom/adresse · ↺ qui remet filtres ET recherche à zéro. */}
      <BarreRecherche style={s.barre} valeur={q} onChange={setQ} placeholder={t('admin.rechercheCompte')}
        nbFiltres={nbFiltres} onFiltres={() => setFeuilleFiltres(true)}
        onReinitialiser={() => { setFiltres({}); setQ(''); }} />

      {nDemandes > 0 && (
        <TouchableOpacity style={s.compteur} activeOpacity={0.7}
          onPress={() => setFiltres(f => ({ ...f, attente: ['avec'] }))}>
          <Text style={s.compteurTxt}>⏳ {t('admin.nDemandes', { n: nDemandes })}</Text>
        </TouchableOpacity>
      )}

      <ScrollView contentContainerStyle={s.corps}>
        {!!err && <Text style={s.err}>⚠️ {err}</Text>}
        {total === 0 ? (
          <EmptyState icon="👥" texte={t('admin.aucunCompte')} aide={t('admin.aucunCompteAide')} />
        ) : sections.map(sec => (
          <View key={sec.cle}>
            {/* Un seul valideur, plusieurs adresses : l'en-tête les réunit. */}
            {sec.cle !== SANS_IVV && (
              <View style={s.tete}>
                <Text style={s.teteTxt}>🐞 {sec.nom}</Text>
                <Text style={s.teteN}>{t('admin.nComptes', { n: sec.comptes.length })}</Text>
              </View>
            )}
            {sec.cle === SANS_IVV && sections.length > 1 && (
              <View style={s.tete}><Text style={s.teteTxt}>{t('admin.sansIVV')}</Text></View>
            )}
            {sec.comptes.map(c => (
              <LigneSwipe key={c.uid} espacement={8}
                onSupprimer={estAdmin ? () => supprimerDepuisListe(c) : undefined}
                onEditer={() => ouvrirEdition(c)}>
                <CarteCompte compte={c} onPress={() => ouvrirFiche(c)} onLongPress={() => menuWeb(c)}
                  busy={busyUid === c.uid} plansCfg={plansCfg}
                  onAccepter={() => accepterDepuisListe(c)} onRefuser={() => refuserDepuisListe(c)} />
              </LigneSwipe>
            ))}
          </View>
        ))}
        <Text style={s.aide}>{t('admin.nComptes', { n: total })}</Text>
      </ScrollView>

      <FeuilleCompte compte={edit} ouvrirEnEdition={editionDirecte} onFermer={() => setEdit(null)} />
      <FeuilleNouveauCompte visible={nouveau} onFermer={() => setNouveau(false)} />
      <FeuilleFiltres visible={feuilleFiltres} groupes={groupes} valeurs={filtres}
        onAppliquer={setFiltres} onFermer={() => setFeuilleFiltres(false)} />
    </View>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  ...stylesForm(UI),
  page: { flex: 1, backgroundColor: UI.bg },
  barre: { margin: 12, marginBottom: 8 },
  compteur: { marginHorizontal: 12, marginBottom: 8, paddingVertical: 8, paddingHorizontal: 12,
              borderRadius: 10, backgroundColor: UI.accentTint, alignItems: 'center' },
  compteurTxt: { fontSize: 12.5, fontWeight: '800', color: UI.accent },
  corps: { padding: 12, paddingBottom: 30 },
  tete: { flexDirection: 'row', alignItems: 'baseline', justifyContent: 'space-between',
          marginTop: 6, marginBottom: 6 },
  teteTxt: { fontSize: 12, fontWeight: '900', color: UI.muted, textTransform: 'uppercase', letterSpacing: 0.4 },
  teteN: { fontSize: 11, color: UI.faint, fontWeight: '700' },
  err: { fontSize: 12.5, color: UI.danger, fontWeight: '700', marginBottom: 8 },
  aide: { fontSize: 11.5, color: UI.faint, textAlign: 'center', marginTop: 12 },
});
